import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { $userStore } from '@lib/store/userManagement';
import { ValidateUtils } from '@shared/utils';
import { BaseInput, Button, ErrorMessage, Modal, Text } from '@shared/widgets';

interface IForm {
  password: string;
  confirmPassword: string;
}
const DEFAULT_VALUES: IForm = { password: '', confirmPassword: '' };

interface IChangePasswordModal {
  show: boolean;
  onClose: () => void;
  userId: string;
}

export const ChangePasswordModal = (props: IChangePasswordModal) => {
  const { control, formState, handleSubmit, watch, setError, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });
  const password = watch('password');
  const confirmPassword = watch('confirmPassword');

  const { t } = useTranslation('user');

  useEffect(() => {
    if (password === confirmPassword) setError('confirmPassword', { message: '' });
  }, [password]);

  const closeHandler = () => {
    reset(DEFAULT_VALUES);
    props.onClose();
  };
  const submitHandler = async (data: IForm) => {
    await $userStore.events.changePassword({
      userId: props.userId,
      password: data.password,
      confirmPassword: data.confirmPassword,
    });
    closeHandler();
  };

  const passwordRequirements = useMemo(
    () => [
      { label: t('sidebar.modals.changePassword.requirements.length'), validator: (password: string) => password.length >= 8 },
      { label: t('sidebar.modals.changePassword.requirements.lowercase'), validator: (password: string) => /[a-z]/.test(password) },
      { label: t('sidebar.modals.changePassword.requirements.uppercase'), validator: (password: string) => /[A-Z]/.test(password) },
      { label: t('sidebar.modals.changePassword.requirements.number'), validator: (password: string) => /\d/.test(password) },
      { label: t('sidebar.modals.changePassword.requirements.special'), validator: (password: string) => /[^a-zA-Z0-9]/.test(password) },
    ],
    [t],
  );
  const passwordRequirementsStatus = useMemo(
    () => passwordRequirements.map((requirement) => ({ ...requirement, isValid: requirement.validator(password || '') })),
    [password, passwordRequirements],
  );
  return (
    <Modal isOpen={props.show} onClose={closeHandler} title={t('sidebar.modals.changePassword.title')} width='332px'>
      <form className='flex flex-col gap-5' noValidate onSubmit={handleSubmit(submitHandler)}>
        <Controller
          name='password'
          control={control}
          rules={{
            required: t('errors.form.required', { ns: 'common' }),
            minLength: { value: 8, message: t('sidebar.modals.changePassword.minLengthError') },
            pattern: { value: ValidateUtils.passwordRegex, message: t('errors.form.password.invalidFormat', { ns: 'common' }) },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} type='password' placeholder={t('placeholders.newPassword', { ns: 'common' })} required />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <div className='flex flex-col gap-2'>
          <Text>{t('sidebar.modals.changePassword.hint')}:</Text>
          <ul className='flex flex-col gap-1'>
            {passwordRequirementsStatus.map((requirement, idx) => (
              <li key={idx} className='flex items-center gap-2'>
                <Text color={requirement.isValid ? 'green' : undefined}>{requirement.isValid ? '✓' : '○'}</Text>
                <Text size='sm'>{requirement.label}</Text>
              </li>
            ))}
          </ul>
        </div>
        <Controller
          name='confirmPassword'
          control={control}
          rules={{
            required: t('errors.form.required', { ns: 'common' }),
            validate: (value) => value === password || t('sidebar.modals.changePassword.mathError'),
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} type='password' placeholder={t('placeholders.repeatNewPassword', { ns: 'common' })} required />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Button className='mt-7' color='primary' fullWidth type='submit' disabled={formState.isSubmitting}>
          {t('buttons.changePassword', { ns: 'common' })}
        </Button>
      </form>
    </Modal>
  );
};
